import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {Box, List, ListItem, SvgIconOwnProps, SvgIconTypeMap} from '@mui/material';
import {OverridableComponent} from '@mui/material/OverridableComponent';
import {EventDetails} from 'src/data/models/events/event';
import {marketTypeOptions} from 'src/data/models/events/marketType';
import {ticketHolderFieldOptions} from 'src/data/models/events/ticketHolderField';
import {countryList} from 'src/shared/countries';

export const Restrictions = ({restrictions}: { restrictions: EventDetails['restrictions'] }) => {
    if (!restrictions) {
        return null;
    }

    const {
        allowAwayFans,
        requireFullCustomerAddress,
        blacklistedCountryCodes = [],
        geoBlockedCountryCodes = [],
        blacklistedNationalityCodes = [],
        blacklistedMarkets = [],
        whitelistedMarkets = [],
        requiredTicketHolderFields = [],
        requireTicketHolderInformationAtOrder = false
    } = restrictions;

    const requiredTicketHolderFieldLabels = requiredTicketHolderFields.map(
        (field) => ticketHolderFieldOptions.find((o) => o.value === field)?.label ?? String(field)
    );

    const {blacklistedCountryNames, geoBlockedCountryNames, blacklistedNationalityNames} =
        getRestrictedCountryAndNationalities(
            blacklistedCountryCodes,
            geoBlockedCountryCodes,
            blacklistedNationalityCodes
        );

    return (
        <div>
            {!allowAwayFans && (
                <RestrictionItem icon={CloseIcon} color="error" text="No away fans allowed"/>
            )}
            {requireFullCustomerAddress && (
                <RestrictionItem
                    icon={CheckIcon}
                    color="success"
                    text="Requires full customer address"
                />
            )}
            {requiredTicketHolderFields.length > 0 && (
                <RestrictionItem
                    icon={CheckIcon}
                    color="success"
                    text={
                        requireTicketHolderInformationAtOrder
                            ? 'Requires ticket holder information when placing the order'
                            : 'Requires ticket holder information'
                    }
                    labelList={requiredTicketHolderFieldLabels}
                />
            )}
            {blacklistedCountryCodes.length > 0 && (
                <RestrictionItem
                    icon={CloseIcon}
                    color="error"
                    text={`${blacklistedCountryCodes.length} Blacklisted ${
                        blacklistedCountryCodes.length === 1 ? 'country' : 'countries'
                    }`}
                    list={blacklistedCountryNames}
                />
            )}
            {geoBlockedCountryCodes.length > 0 && (
                <RestrictionItem
                    icon={CloseIcon}
                    color="error"
                    text={`${geoBlockedCountryCodes.length} Geo-blocked ${
                        geoBlockedCountryCodes.length === 1 ? 'country' : 'countries'
                    }`}
                    list={geoBlockedCountryNames}
                />
            )}
            {blacklistedNationalityCodes.length > 0 && (
                <RestrictionItem
                    icon={CloseIcon}
                    color="error"
                    text={`${blacklistedNationalityCodes.length} Blacklisted ${
                        blacklistedNationalityCodes.length === 1 ? 'nationality' : 'nationalities'
                    }`}
                    list={blacklistedNationalityNames}
                />
            )}
            {blacklistedMarkets.length > 0 && (
                <RestrictionItem
                    icon={CloseIcon}
                    color="error"
                    text={`${blacklistedMarkets.length} Blacklisted ${
                        blacklistedMarkets.length === 1 ? 'market' : 'markets'
                    }`}
                    marketList={blacklistedMarkets}
                />
            )}
            {whitelistedMarkets.length > 0 && (
                <RestrictionItem
                    icon={CheckIcon}
                    color="success"
                    text={`${whitelistedMarkets.length} Whitelisted ${
                        whitelistedMarkets.length === 1 ? 'market' : 'markets'
                    }`}
                    marketList={whitelistedMarkets}
                />
            )}
        </div>
    );
};

const RestrictionItem = ({
                             icon: Icon,
                             color,
                             text,
                             list,
                             marketList,
                             labelList,
                         }: {
    icon: OverridableComponent<SvgIconTypeMap>;
    color: SvgIconOwnProps['color'];
    text?: string;
    list?: CountryRestrictionListType[];
    marketList?: MarketTypeValue[];
    labelList?: string[];
}) => {
    return (
        <div>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: (theme) => theme.spacing(1),
                }}
            >
                <Icon color={color} />
                {text}
            </Box>

            {list && <CountryRestrictionsList list={list} />}
            {marketList && <MarketRestrictionsList list={marketList} />}
            {labelList && labelList.length > 0 && <LabelList list={labelList} />}
        </div>
    );
};

const LabelList = ({list}: { list: string[] }) => {
    return (
        <List
            sx={{
                paddingTop: 0,
                marginTop: 0,
                marginBottom: (theme) => theme.spacing(1),
                fontSize: '.875rem',
                marginLeft: 1,
            }}
        >
            {list.map((label) => (
                <ListItem sx={{color: (theme) => theme.colors.darkGrey}} key={label}>
                    {label}
                </ListItem>
            ))}
        </List>
    );
};

type CountryRestrictionListType = (typeof countryList)[number];

const CountryRestrictionsList = ({list}: { list: CountryRestrictionListType[] }) => {
    return (
        <div>
            <List
                sx={{
                    paddingTop: 0,
                    marginTop: 0,
                    marginBottom: (theme) => theme.spacing(1),
                    fontSize: '.875rem',
                    marginLeft: 1,
                }}
            >
                {list.map((country) => (
                    <ListItem
                        sx={{
                            color: (theme) => theme.colors.darkGrey,
                        }}
                        key={country.label}
                    >
                        {country.label}
                    </ListItem>
                ))}
            </List>
        </div>
    );
};

type MarketTypeValue = (typeof marketTypeOptions)[number]['value'];

const MarketRestrictionsList = ({ list }: { list: MarketTypeValue[] }) => {
    return (
        <List
            sx={{
                paddingTop: 0,
                marginTop: 0,
                marginBottom: (theme) => theme.spacing(1),
                fontSize: '.875rem',
                marginLeft: 1,
            }}
        >
            {list.map((market) => {
                const label = marketTypeOptions.find((o) => o.value === market)?.label ?? String(market);

                return (
                    <ListItem sx={{ color: (theme) => theme.colors.darkGrey }} key={String(market)}>
                        {label}
                    </ListItem>
                );
            })}
        </List>
    );
};

const getRestrictedCountryAndNationalities = (
    blacklistedCountryCodes: string[],
    geoBlockedCountryCodes: string[],
    blacklistedNationalityCodes: string[]
) => {
    const countryCodes = new Set(blacklistedCountryCodes);
    const geoBlockedCodes = new Set(geoBlockedCountryCodes);
    const nationalityCodes = new Set(blacklistedNationalityCodes);

    return {
        blacklistedCountryNames: countryList.filter((c) => countryCodes.has(c.value)),
        geoBlockedCountryNames: countryList.filter((c) => geoBlockedCodes.has(c.value)),
        blacklistedNationalityNames: countryList.filter((c) => nationalityCodes.has(c.value)),
    };
};
